import { useCallback, useEffect } from "react";
import { Cordwood, CreateCordwood, UpdateCordwood } from "../types/models/Cordwood.d";
import { UpdateEntityCordwood } from "../types/models/Cordwood";
import { useGetCordwoodListQuery, useInsertCordwoodMutation, useUpdateCordwoodMutation, useDeleteCordwoodMutation, } from "../apis/Cordwood.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useCordwood(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertCordwoodMutation, { 
        isLoading: insertCordwoodLoading, 
        isSuccess: insertCordwoodIsSuccess,
        isError: insertCordwoodIsError,
        error: insertCordwoodError,
    }] = useInsertCordwoodMutation()
    const [updateCordwoodMutation, { 
        isLoading: updateCordwoodLoading,
        isSuccess: updateCordwoodIsSuccess,
        isError: updateCordwoodIsError,
        error: updateCordwoodError,
    }] = useUpdateCordwoodMutation()
    const [deleteCordwoodMutation, { 
        isLoading: deleteCordwoodLoading,
        isSuccess: deleteCordwoodIsSuccess,
        isError: deleteCordwoodIsError,
        error: deleteCordwoodError,
    }] = useDeleteCordwoodMutation()

    const loading =
        insertCordwoodLoading ||
        updateCordwoodLoading ||
        deleteCordwoodLoading ||
        false

    const insertCordwood = useCallback(async (cordwood: CreateCordwood, redirectPath: string|undefined) => {
        try {
            const data = await insertCordwoodMutation(cordwood).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertCordwoodMutation, navigate])
    
    useEffect(() => {
        if (insertCordwoodIsSuccess) {
            openSuccessAlert('Cordwood created')
        } else if (insertCordwoodIsError) {
            console.log("add error", insertCordwoodError)
            openFailAlert(t`Cordwood add error: ` + insertCordwoodError?.data?.message)
        }
    }, [
        insertCordwoodIsSuccess,
        insertCordwoodIsError,
        insertCordwoodError
    ]);

    const updateCordwood = useCallback(async (cordwood: UpdateEntityCordwood, redirectPath: string|undefined) => {
        try {
            const data = await updateCordwoodMutation(cordwood).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateCordwoodMutation, navigate])
    
    useEffect(() => {
        if (updateCordwoodIsSuccess) {
            openSuccessAlert(t`Cordwood updated`)
        } else if (updateCordwoodIsError) {
            console.log("update error", updateCordwoodError)
            openFailAlert(t`Cordwood update error: ` + updateCordwoodError?.data?.message)
        }
    }, [
        updateCordwoodIsSuccess,
        updateCordwoodIsError,
        updateCordwoodError
    ]);

    const deleteCordwood = useCallback((cordwood: Cordwood, redirectPath: string|undefined) => {
        openDialog(async () => {
            cordwood = {
                ...cordwood,
            }
            try {
                const { data } = await deleteCordwoodMutation(cordwood);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteCordwoodMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteCordwoodIsSuccess) {
            openSuccessAlert(t`Cordwood deleted`)
        } else if (deleteCordwoodIsError) {
            console.log("delete error", deleteCordwoodError)
            openFailAlert(t`Cordwood delete error: ` + deleteCordwoodError?.data?.message)
        }
    }, [
        deleteCordwoodIsSuccess,
        deleteCordwoodIsError,
        deleteCordwoodError
    ]);

    return {
        loading,
        insertCordwood,
        updateCordwood,
        deleteCordwood
    }
}
